import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, ShieldCheck, Zap, ListChecks, Wand2, Trash2, Plus, RefreshCw, PackageSearch } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtR = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SEVERITY = {
  red:    { cls: 'bg-rose-950 border-rose-600 text-rose-300',       row: 'border-l-4 border-l-rose-500 bg-rose-950/20' },
  orange: { cls: 'bg-amber-950 border-amber-600 text-amber-300',    row: 'border-l-4 border-l-amber-500 bg-amber-950/10' },
  green:  { cls: 'bg-emerald-950 border-emerald-600 text-emerald-300', row: '' },
};

export default function PricingMonitor() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [days, setDays] = useState(30);
  const [variance, setVariance] = useState(20);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ flagged: 0, red: 0, orange: 0 });
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [aliases, setAliases] = useState([]);
  const [newAlias, setNewAlias] = useState({ alias: '', canonical: '' });
  const [saving, setSaving] = useState(false);

  const fetchExceptions = useCallback(async () => {
    setLoading(true);
    try {
      const [ex, pr] = await Promise.all([
        axios.get(`${API_URL}/api/pricing-monitor/exceptions?days=${days}&variance_pct=${variance}`, { headers }),
        axios.get(`${API_URL}/api/pricing-monitor/products?days=${days}`, { headers }),
      ]);
      setSummary(ex.data.summary || {});
      setRows(ex.data.rows || []);
      setProducts(pr.data.products || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load pricing monitor');
    } finally {
      setLoading(false);
    }
  }, [days, variance]);

  const fetchAliases = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pricing-monitor/aliases`, { headers });
      setAliases(res.data.aliases || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load aliases');
    }
  }, []);

  useEffect(() => { fetchExceptions(); }, [fetchExceptions]);
  useEffect(() => { fetchAliases(); }, [fetchAliases]);

  const saveAlias = async () => {
    if (!newAlias.alias.trim() || !newAlias.canonical.trim()) {
      toast.error('Both fields are required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/pricing-monitor/aliases`, newAlias, { headers });
      toast.success(`"${newAlias.alias}" → "${newAlias.canonical}" saved`);
      setNewAlias({ alias: '', canonical: '' });
      await fetchAliases();
      await fetchExceptions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteAlias = async (alias) => {
    if (!window.confirm(`Delete alias "${alias}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/pricing-monitor/aliases/${encodeURIComponent(alias)}`, { headers });
      toast.success('Deleted');
      await fetchAliases();
      await fetchExceptions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="pricing-monitor-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-cyan-400" /> Pricing Monitor
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Auto-flag same-day price spreads, sales below/above average, and below-minimum pricing.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div>
              <Label className="text-slate-400 text-xs">Window (days)</Label>
              <Input type="number" min={1} max={365} value={days}
                     onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 30))}
                     className="w-24 bg-slate-900 border-slate-700 text-white h-9" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Variance %</Label>
              <Input type="number" min={1} max={100} value={variance}
                     onChange={(e) => setVariance(Math.max(1, Number(e.target.value) || 20))}
                     className="w-24 bg-slate-900 border-slate-700 text-white h-9" />
            </div>
            <Button variant="outline" onClick={fetchExceptions} disabled={loading}
                    className="bg-slate-900 border-slate-700 text-slate-200 mt-4">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard icon={ListChecks}  label="Sales in window" value={summary.total_sales_in_window ?? 0} colour="from-slate-600 to-slate-700" />
          <SummaryCard icon={AlertTriangle} label="Flagged"       value={summary.flagged ?? 0} colour="from-amber-500 to-amber-600" />
          <SummaryCard icon={Zap}          label="Red exceptions" value={summary.red ?? 0} colour="from-rose-500 to-rose-600" />
          <SummaryCard icon={PackageSearch} label="Products"      value={summary.distinct_products ?? 0} colour="from-cyan-500 to-cyan-600" />
        </div>

        <Tabs defaultValue="exceptions">
          <TabsList className="bg-slate-800 border-slate-700">
            <TabsTrigger value="exceptions" data-testid="tab-exceptions">Exceptions</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Product Groups</TabsTrigger>
            <TabsTrigger value="aliases" data-testid="tab-aliases">Name Aliases</TabsTrigger>
          </TabsList>

          <TabsContent value="exceptions">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-2"><CardTitle className="text-white text-base">Flagged sales</CardTitle></CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader className="bg-slate-900/50">
                    <TableRow className="border-slate-700 hover:bg-transparent">
                      <TableHead className="text-slate-400">Date</TableHead>
                      <TableHead className="text-slate-400">Salesman</TableHead>
                      <TableHead className="text-slate-400">Raw description</TableHead>
                      <TableHead className="text-slate-400">Canonical</TableHead>
                      <TableHead className="text-slate-400 text-right">Price</TableHead>
                      <TableHead className="text-slate-400 text-right">Avg</TableHead>
                      <TableHead className="text-slate-400">Reasons</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-slate-500 text-center py-8">
                        No exceptions in this window. Nice pricing discipline.
                      </TableCell></TableRow>
                    )}
                    {rows.filter(r => r.reasons?.length > 0).map(r => (
                      <TableRow key={r.id} className={`border-slate-700 ${SEVERITY[r.severity]?.row || ''}`}
                                data-testid={`pricing-exception-${r.id}`}>
                        <TableCell className="text-slate-300 text-xs">{r.date}</TableCell>
                        <TableCell className="text-slate-300 text-xs">{r.salesman_name || '—'}</TableCell>
                        <TableCell className="text-slate-400 text-xs italic">{r.part_description || '—'}</TableCell>
                        <TableCell className="text-white font-semibold text-xs">{r.canonical_product}</TableCell>
                        <TableCell className="text-white text-right font-mono">{fmtR(r.unit_price)}</TableCell>
                        <TableCell className="text-slate-400 text-right font-mono text-xs">{fmtR(r.avg_price)}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {r.reasons.map((rz, i) => (
                              <Badge key={i} className={`${SEVERITY[r.severity]?.cls} text-[10px]`}
                                     title={rz.detail}>
                                <AlertTriangle className="h-2.5 w-2.5 mr-1" /> {rz.code}
                              </Badge>
                            ))}
                          </div>
                          <div className="text-[10px] text-slate-400 mt-1">
                            {r.reasons.map(rz => rz.detail).join(' · ')}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="products">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base">Products (rolled up by canonical name)</CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader className="bg-slate-900/50">
                    <TableRow className="border-slate-700 hover:bg-transparent">
                      <TableHead className="text-slate-400">Canonical</TableHead>
                      <TableHead className="text-slate-400 text-right">Sales</TableHead>
                      <TableHead className="text-slate-400 text-right">Qty</TableHead>
                      <TableHead className="text-slate-400 text-right">Lowest</TableHead>
                      <TableHead className="text-slate-400 text-right">Avg</TableHead>
                      <TableHead className="text-slate-400 text-right">Median</TableHead>
                      <TableHead className="text-slate-400 text-right">Highest</TableHead>
                      <TableHead className="text-slate-400 text-right">Spread %</TableHead>
                      <TableHead className="text-slate-400">Variants</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map(p => (
                      <TableRow key={p.canonical} className="border-slate-700" data-testid={`product-row-${p.canonical}`}>
                        <TableCell className="text-white font-semibold text-xs">{p.canonical}</TableCell>
                        <TableCell className="text-slate-300 text-right">{p.sales_count}</TableCell>
                        <TableCell className="text-slate-300 text-right">{p.quantity}</TableCell>
                        <TableCell className="text-emerald-400 text-right font-mono text-xs">{fmtR(p.lowest)}</TableCell>
                        <TableCell className="text-slate-200 text-right font-mono text-xs">{fmtR(p.average)}</TableCell>
                        <TableCell className="text-slate-400 text-right font-mono text-xs">{fmtR(p.median)}</TableCell>
                        <TableCell className="text-rose-400 text-right font-mono text-xs">{fmtR(p.highest)}</TableCell>
                        <TableCell className={`text-right font-semibold text-xs ${p.spread_pct > 50 ? 'text-rose-400' : p.spread_pct > 20 ? 'text-amber-400' : 'text-emerald-400'}`}>
                          {p.spread_pct}%
                        </TableCell>
                        <TableCell className="text-slate-400 text-[10px]">
                          {p.variants.map(v => `${v.raw} (${v.count})`).join(', ')}
                        </TableCell>
                      </TableRow>
                    ))}
                    {products.length === 0 && (
                      <TableRow><TableCell colSpan={9} className="text-slate-500 text-center py-8">No sales in this window.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="aliases">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Wand2 className="h-4 w-4 text-cyan-400" /> Product name aliases
                </CardTitle>
                <p className="text-slate-400 text-xs">
                  Map raw descriptions (typos, variants) to a canonical name. Defaults ship with common ones —
                  add your own below.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                  <div>
                    <Label className="text-slate-300 text-xs">Alias (raw variant)</Label>
                    <Input value={newAlias.alias}
                           onChange={(e) => setNewAlias({ ...newAlias, alias: e.target.value })}
                           placeholder="e.g. wiper machanisim"
                           className="bg-slate-900 border-slate-700 text-white"
                           data-testid="alias-input" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">Canonical name</Label>
                    <Input value={newAlias.canonical}
                           onChange={(e) => setNewAlias({ ...newAlias, canonical: e.target.value })}
                           placeholder="e.g. Wiper Mechanism"
                           className="bg-slate-900 border-slate-700 text-white"
                           data-testid="canonical-input" />
                  </div>
                  <Button onClick={saveAlias} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="save-alias-btn">
                    <Plus className="h-4 w-4 mr-1" /> Save
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700 hover:bg-transparent">
                        <TableHead className="text-slate-400">Alias</TableHead>
                        <TableHead className="text-slate-400">→ Canonical</TableHead>
                        <TableHead className="text-slate-400">Source</TableHead>
                        <TableHead className="text-slate-400 w-16"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {aliases.map(a => (
                        <TableRow key={a.alias + '__' + a.canonical} className="border-slate-700" data-testid={`alias-row-${a.alias}`}>
                          <TableCell className="text-slate-300 text-xs font-mono">{a.alias}</TableCell>
                          <TableCell className="text-white text-xs font-semibold">{a.canonical}</TableCell>
                          <TableCell>
                            <Badge className={a.source === 'custom' ? 'bg-cyan-900 border-cyan-600 text-cyan-300' : 'bg-slate-900 border-slate-600 text-slate-400'}>
                              {a.source}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {a.source === 'custom' && (
                              <Button size="sm" variant="ghost" onClick={() => deleteAlias(a.alias)}
                                      className="h-7 w-7 p-0 text-rose-400 hover:text-rose-300">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

function SummaryCard({ icon: Icon, label, value, colour }) {
  return (
    <Card className={`bg-gradient-to-br ${colour} border-0 text-white`}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-1">
          <Icon className="h-4 w-4" />
          <span className="text-xs opacity-90">{label}</span>
        </div>
        <div className="text-2xl font-extrabold">{value}</div>
      </CardContent>
    </Card>
  );
}
